import { createMultiParser } from 'nuqs';

/**
 * A list of ids in the URL.
 *
 * Read from every value the key carries, not the first. The router parses the
 * search before nuqs sees it, so `searchTagIds=[1,2]` arrives as an array and
 * the adapter spreads it across repeated keys; reading only the first would
 * narrow by tag 1 and drop tag 2 without saying so. One value can also name
 * several ids on its own: a source filter written by the page this replaces
 * reads `searchSourceIds=["11,12","13"]`, its options still carrying the
 * groups they were chosen as.
 *
 * Written as a JSON array of strings, which is the only spelling that page
 * can read. It deserialises an array query parameter with `JSON.parse`, so a
 * comma-separated value throws there and a bare number reads as a non-array
 * that narrows by nothing — and the elements have to be strings, because its
 * source filter splits each one on commas
 * (`app/pods/data-index/type/list/controller.ts:519,598`), which a number
 * cannot do. Its tag filter calls `toString()` and takes either. Both apps
 * serve these URLs while the rollout flag is off, so a link written here has
 * to open there too.
 */

function jsonList(value: string): string[] {
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

/** Every positive integer a value names, in the order it names them. */
export function parseIdList(value: string): number[] {
  const parts = value.trimStart().startsWith('[') ? jsonList(value) : [value];

  return parts
    .flatMap((part) => part.split(','))
    .map((part) => Number(part.trim()))
    .filter((id) => Number.isInteger(id) && id > 0);
}

export const parseAsIdList = createMultiParser({
  parse: (values: readonly string[]) => values.flatMap(parseIdList),
  // One value, so the key carries a single JSON array rather than repeating.
  serialize: (ids: readonly number[]) => [JSON.stringify(ids.map(String))],
  eq: (a: readonly number[], b: readonly number[]) => a.length === b.length && a.every((id, i) => id === b[i]),
});
